// import hooks
import { useMemo } from 'react';
import { useGetOptionsForCLA } from 'hooks/useGetOptionsForCLA';
import { useTranslation } from 'react-i18next';
import { useGetBuhHandlingOptions } from '../components/Sections/Surcharges/hooks/useGetBuhHandlingOptions';

// import components
import { singleSelectValueFormatter } from 'components/shared/DataGrid/utils/valueFormatters/singleSelectValueFormatter';
import {
  EmployerCostSettingsFlags,
  EmployerCostSettingsResponse
} from 'components/admin-portal/Tables/Employer/EmployerCostSettings/EmployerCostSettings.types';
import {
  booleanValueGetterByField,
  dateFormatter,
  nestedFieldValueGetter
} from 'utils/grid';
import { GridColDef } from '@mui/x-data-grid-pro';
import BooleanCell from 'components/shared/Table/TableCell/DataGrid/BooleanCell';

export const useGetColumnDefs = (): GridColDef[] => {
  const { t } = useTranslation('common');

  const claOptions = useGetOptionsForCLA();
  const buhHandlingOptions = useGetBuhHandlingOptions();

  return useMemo(
    () => [
      {
        field: 'DEFAULT',
        type: 'boolean',
        headerName: t('employerCostSettings.grid.flags.DEFAULT'),
        valueGetter: (_, row: EmployerCostSettingsResponse) =>
          booleanValueGetterByField(EmployerCostSettingsFlags.DEFAULT, row),
        renderCell: BooleanCell,
        minWidth: 80
      },
      {
        field: 'name',
        headerName: t('employerCostSettings.grid.name'),
        type: 'string',
        hideable: false,
        sortable: false,
        minWidth: 200,
        flex: 1
      },
      {
        field: 'nameBrief',
        type: 'string',
        headerName: t('employerCostSettings.grid.nameBrief'),
        minWidth: 200,
        flex: 1
      },
      {
        field: 'claId',
        type: 'singleSelect',
        headerName: t('employerCostSettings.grid.claId'),
        valueOptions: claOptions,
        valueFormatter: (value: string | number) =>
          singleSelectValueFormatter(value, claOptions),
        minWidth: 100
      },
      {
        field: 'buhHandlingSvc',
        type: 'singleSelect',
        headerName: t('employerCostSettings.grid.buhHandlingSvc'),
        valueOptions: buhHandlingOptions,
        valueFormatter: (value: string | number) =>
          singleSelectValueFormatter(value, buhHandlingOptions),
        minWidth: 250,
        flex: 1
      },
      {
        field: 'valid.from',
        valueGetter: (_, row: EmployerCostSettingsResponse) =>
          nestedFieldValueGetter('valid.from', row),
        valueFormatter: dateFormatter,
        type: 'date',
        headerName: t('validFrom'),
        minWidth: 100
      },
      {
        field: 'valid.to',
        valueGetter: (_, row: EmployerCostSettingsResponse) =>
          nestedFieldValueGetter('valid.to', row),
        valueFormatter: dateFormatter,
        type: 'date',
        headerName: t('validTo'),
        minWidth: 100
      }
    ],
    [t, claOptions, buhHandlingOptions]
  );
};
